import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import PageContainer from "../../../components/PageContainer/PageContainer";
import { Button } from "@repo/ui";
import { useAppSelector, useAppDispatch } from "../../../hooks/useAppDispatch";
import { useNavigate, useParams } from "react-router-dom";
import placeholderImage from "../../../assets/placeholders/picture.webp";
import { getProduct, Product } from "../../../services/productApiService";
import { colors } from "@repo/ui";
import { useMediaQuery } from "react-responsive";
import BackButton from "../../../components/BackButton";
import StepIndicator from "../../../components/StepIndicator/StepIndicator";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import { fetchDonationPricing } from "../../../redux/slices/pricingSlice";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";
// Import the new exchange rate service call
import { getLastExchangeRate } from "../../../services/exchangeRateApiService";
import i18n from "../../../i18n";
import { gsap } from "gsap";

const LOCALE = "modules_Guests_pages_PaymentPreviewPage";

interface FullCartProduct {
  local: {
    productID: string;
    quantity: number;
    value: number;
  };
  api: Product;
}

const PaymentPreviewPage: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const cartItems = useAppSelector((state) => state.guestCart.items);
  const dispatch = useAppDispatch();
  const donationPricing = useSelector(
    (state: RootState) => state.pricing.donationState
  );

  const { id } = useParams<{ id: string }>();
  const giftTableIDFromState = useAppSelector(
    (state: RootState) => state.guestGiftTable.ID
  );
  const giftTableID = id || giftTableIDFromState;

  const [fullCartProducts, setFullCartProducts] = useState<FullCartProduct[]>(
    []
  );
  // State to store the exchange rate value (default multiplier of 1)
  const [exchangeRate, setExchangeRate] = useState<number>(1);

  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const summaryContentId = useId();
  const summaryContainerRef = useRef<HTMLDivElement>(null);
  const summaryWrapperRef = useRef<HTMLDivElement>(null);
  const summaryInnerRef = useRef<HTMLDivElement>(null);

  // Fetch product details
  useEffect(() => {
    const fetchProducts = async () => {
      const productDetails = await Promise.all(
        cartItems.map(async (item) => {
          const apiProduct = await getProduct(item.productID);
          return { local: item, api: apiProduct };
        })
      );
      setFullCartProducts(productDetails);
    };

    fetchProducts();
  }, [cartItems]);

  // Fetch the latest exchange rate
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const lastRate = await getLastExchangeRate();
        // Assume lastRate.rate holds the conversion multiplier
        setExchangeRate(lastRate.rate);
      } catch (error) {
        console.error("Error fetching exchange rate", error);
      }
    };
    fetchExchangeRate();
  }, []);

  const totalPrice = fullCartProducts.reduce(
    (total, item) => total + item.local.value * item.local.quantity,
    0
  );

  useEffect(() => {
    if (totalPrice > 0) {
      dispatch(
        fetchDonationPricing({
          price: totalPrice,
          country: "MX",
        })
      );
    }
  }, [totalPrice, dispatch]);

  // Decide whether to use the fetched exchange rate or 1 depending on the language.
  // If language is "es", use 1; otherwise, use the exchange rate.
  const conversionRate = i18n.language === "es" ? 1 : exchangeRate;

  // Calculate preview values using the conversion rate
  const subtotalWithBrokerFee =
    (donationPricing.totalCharge - donationPricing.donationFeeTotal) /
    conversionRate;
  const serviceFee = donationPricing.donationFee / conversionRate;
  const serviceFeeTax = donationPricing.donationFeeTax / conversionRate;
  const finalTotal = donationPricing.totalCharge / conversionRate;

  const summaryToggleLabel = isSummaryExpanded
    ? t("collapsePurchaseSummary", {
        defaultValue: "Ocultar desglose de compra",
      })
    : t("expandPurchaseSummary", {
        defaultValue: "Mostrar desglose de compra",
      });

  useLayoutEffect(() => {
    if (!summaryContainerRef.current || !summaryWrapperRef.current) {
      return;
    }

    gsap.set(summaryContainerRef.current, { paddingTop: 24 });
    gsap.set(summaryWrapperRef.current, { height: 0, autoAlpha: 0 });
  }, []);

  useEffect(() => {
    const container = summaryContainerRef.current;
    const wrapper = summaryWrapperRef.current;
    const inner = summaryInnerRef.current;

    if (!container || !wrapper || !inner) {
      return;
    }

    const contentHeight = inner.scrollHeight;
    const tl = gsap.timeline({
      defaults: { duration: 0.35, ease: "power2.out" },
    });

    if (isSummaryExpanded) {
      tl.to(
        wrapper,
        {
          height: contentHeight,
          autoAlpha: 1,
          onComplete: () => {
            gsap.set(wrapper, { height: "auto" });
          },
        },
        0
      ).to(
        container,
        {
          paddingTop: 32,
        },
        0
      );
    } else {
      tl.to(
        wrapper,
        {
          height: 0,
          autoAlpha: 0,
          onComplete: () => {
            gsap.set(wrapper, { height: 0 });
          },
        },
        0
      ).to(
        container,
        {
          paddingTop: 24,
        },
        0
      );
    }

    return () => {
      tl.kill();
    };
  }, [
    isSummaryExpanded,
    subtotalWithBrokerFee,
    serviceFee,
    serviceFeeTax,
    finalTotal,
    donationPricing.totalCharge,
  ]);

  return (
    <PageContainer startColor="white" endColor={colors.background}>
      {!isMobile && (
        <div className="py-4">
          <StepIndicator
            steps={[t("step_message"), t("step_summary"), t("step_payment")]}
            currentStep={2}
            progressPercentage={50}
          />
        </div>
      )}

      <div className="py-16 flex justify-center">
        <div className="flex flex-col md:flex-row w-full max-w-6xl bg-white shadow-md rounded-lg">
          <div className="flex-1 mb-8 md:mb-0 md:mr-8 p-8">
            <BackButton
              text={t("goBack")}
              onClick={() =>
                navigate(`/guests/checkout/dedication/${giftTableID}`, {
                  state: { fromPreviewPage: true },
                })
              }
            />
            <h2 className="text-2xl font-bold mb-4">{t("summaryTitle")}</h2>
            <p className="text-gray-600 mb-6">{t("summaryDescription")}</p>
            <div className="space-y-4 min-h-[40vh] max-h-[40vh] overflow-y-auto">
              {fullCartProducts.map((item) => (
                <div
                  key={item.local.productID}
                  className="flex justify-between items-center p-4 border rounded-lg shadow-md bg-white"
                >
                  <div className="flex items-center space-x-4">
                    <img
                      src={item.api.pictures?.[0]?.trim() || placeholderImage}
                      alt={item.api.title}
                      className="w-24 h-24 object-cover"
                    />
                    <div>
                      <h3 className="font-bold">{item.api.title}</h3>
                      <p className="text-sm">
                        {t("quantity")}: {item.local.quantity}
                      </p>
                      <p className="font-semibold text-lg">
                        {(
                          item.api.display_total_charge ??
                          item.api.display_price
                        )?.toLocaleString("es-MX", {
                          style: "currency",
                          currency: "MXN",
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!isMobile && (
            <div className="w-full md:w-1/3 bg-primary text-white p-8 rounded-lg shadow-md">
              <h2 className="text-xl font-bold mb-4">
                {t("purchaseBreakdown")}
              </h2>
              <div className="border-b border-white mb-4 pb-4">
                <div className="flex justify-between">
                  <span>{t("subtotal")}</span>
                  <span className="ml-8">
                    {subtotalWithBrokerFee.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t("serviceFee")}</span>
                  <span className="ml-8">
                    {serviceFee?.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t("tax", { defaultValue: "IVA" })}</span>
                  <span className="ml-8">
                    {serviceFeeTax?.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })}
                  </span>
                </div>
              </div>
              <div className="flex justify-between text-lg font-bold mb-4">
                <span>{t("total")}</span>
                <span className="ml-8">
                  {finalTotal?.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                  })}
                </span>
              </div>
              <Button
                label={t("proceedToPayment")}
                onClick={() =>
                  navigate(`/guests/checkout/payment/${giftTableID}`)
                }
                className="w-full bg-white text-primary"
              />
            </div>
          )}
        </div>
      </div>

      {isMobile && (
        <div
          ref={summaryContainerRef}
          className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl bg-primary px-4 pb-4 text-white"
        >
          <button
            type="button"
            className="absolute left-1/2 top-2 flex h-8 w-16 -translate-x-1/2 items-center justify-center rounded-full focus:outline-none focus-visible:outline-none"
            onClick={() => setIsSummaryExpanded((prev) => !prev)}
            aria-expanded={isSummaryExpanded}
            aria-controls={summaryContentId}
            aria-label={summaryToggleLabel}
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            <span className="block h-1.5 w-10 rounded-full bg-white/80" />
          </button>

          <div
            ref={summaryWrapperRef}
            id={summaryContentId}
            aria-hidden={!isSummaryExpanded}
            className="overflow-hidden"
          >
            <div ref={summaryInnerRef}>
              <h2 className="mb-2 text-lg font-semibold">
                {t("purchaseBreakdown")}
              </h2>
              <div className="space-y-2 border-b border-white/30 pb-3 text-sm text-white/90">
                <div className="flex justify-between">
                  <span>{t("subtotal")}</span>
                  <span className="ml-8">
                    {subtotalWithBrokerFee.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t("serviceFee")}</span>
                  <span className="ml-8">
                    {serviceFee?.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t("tax", { defaultValue: "IVA" })}</span>
                  <span className="ml-8">
                    {serviceFeeTax?.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`flex items-center justify-between text-lg font-bold ${
              isSummaryExpanded ? "mt-4" : "mt-2"
            } mb-3`}
          >
            <span>{t("total")}</span>
            <span className="ml-8">
              {finalTotal?.toLocaleString("es-MX", {
                style: "currency",
                currency: "MXN",
              })}
            </span>
          </div>
          <Button
            label={t("proceedToPayment")}
            onClick={() => navigate(`/guests/checkout/payment/${giftTableID}`)}
            className="mt-1 w-full bg-white text-primary"
          />
        </div>
      )}
    </PageContainer>
  );
};

export default withTranslations(LOCALE)(PaymentPreviewPage);
